/*
 * Social Feed Grid — Swiper init for slider mode.
 *
 * forEach over every [data-social-feed] block on the page. For
 * blocks with data-layout="slider", rearrange the flat content_for
 * children into Swiper's expected DOM (.swiper > .swiper-wrapper >
 * .swiper-slide) and instantiate Swiper with per-breakpoint
 * slidesPerView + spaceBetween read from the block's CSS custom
 * properties (--sf-cols-{d,t,m} / --sf-col-gap-{d,t,m}).
 *
 * Grid-mode blocks are a no-op — pure CSS handles layout.
 *
 * Re-runs on shopify:section:load so customizer reloads pick up
 * the new state. shopify:section:unload destroys the swiper +
 * removes listeners.
 *
 * Swiper is the global UMD vendored at assets/swiper-bundle.min.js
 * (loaded by snippets/scripts.liquid before this file). We grab it
 * off window — no ESM import needed.
 */

const Swiper = window.Swiper;
const SELECTOR = '[data-social-feed]';
const INSTANCE_KEY = '_socialFeedInstance';

class SocialFeedSlider {
  constructor(root) {
    this.root = root;
    this.swiper = null;
    this.abortController = new AbortController();

    this.init();
  }

  init() {
    const layout = this.root.dataset.layout || 'grid';
    if (layout !== 'slider') return;

    const styles = getComputedStyle(this.root);
    const colsD = parseFloat(styles.getPropertyValue('--sf-cols-d')) || 5;
    const colsT = parseFloat(styles.getPropertyValue('--sf-cols-t')) || 4;
    const colsM = parseFloat(styles.getPropertyValue('--sf-cols-m')) || 2;
    const gapD = parseFloat(styles.getPropertyValue('--sf-col-gap-d')) || 8;
    const gapT = parseFloat(styles.getPropertyValue('--sf-col-gap-t')) || 8;
    const gapM = parseFloat(styles.getPropertyValue('--sf-col-gap-m')) || 8;

    const loop = this.root.dataset.loop === 'true';
    const autoplayOn = this.root.dataset.autoplay === 'true';
    const autoplayDelay = (parseFloat(this.root.dataset.autoplaySpeed) || 5) * 1000;

    // Collect direct children of root — these are the rendered image /
    // video / @app blocks from content_for 'blocks'. Move them into a
    // .swiper > .swiper-wrapper structure, wrapping each in
    // .swiper-slide as we go.
    const children = Array.from(this.root.children);
    if (children.length === 0) return;

    const swiperEl = document.createElement('div');
    swiperEl.className = 'swiper social-feed-grid__swiper';
    const wrapperEl = document.createElement('div');
    wrapperEl.className = 'swiper-wrapper';

    children.forEach((child) => {
      const slide = document.createElement('div');
      slide.className = 'swiper-slide';
      slide.appendChild(child);
      wrapperEl.appendChild(slide);
    });

    swiperEl.appendChild(wrapperEl);
    this.root.replaceChildren(swiperEl);

    this.swiper = new Swiper(swiperEl, {
      slidesPerView: colsM,
      spaceBetween: gapM,
      loop: loop,
      grabCursor: true,
      autoplay: autoplayOn
        ? { delay: autoplayDelay, disableOnInteraction: false, pauseOnMouseEnter: true }
        : false,
      breakpoints: {
        // Mobile (default) → tablet at 768px → desktop at 992px.
        768: {
          slidesPerView: colsT,
          spaceBetween: gapT,
        },
        992: {
          slidesPerView: colsD,
          spaceBetween: gapD,
        },
      },
    });

    this.root.classList.add('is-initialized');
  }

  destroy() {
    this.abortController.abort();
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

function initAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) return;
    el[INSTANCE_KEY] = new SocialFeedSlider(el);
  });
}

function destroyAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) {
      el[INSTANCE_KEY].destroy();
      el[INSTANCE_KEY] = null;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (e) => initAll(e.target));
document.addEventListener('shopify:section:unload', (e) => destroyAll(e.target));
